/**
 * Create a section for history listing. Used for main categories (darker header background-color).
 */
import * as React from 'react';

import colors from '../../config/theme/colors';
import { styleDocument } from '../../config/theme/componentTheme';
import { Container, Item } from '../Grid';
import ToolTip from '../ToolTip';
import { createID } from '../../utility/appendIDs';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/system';
import { MoreVert } from '@mui/icons-material';
import { IconButton, Menu } from '@mui/material';
import { blurActiveElement } from 'Utility/domFocus';

const StyledMenuIcon = styled(({ onClick }: { onClick?: (e: React.MouseEvent<Element>) => void }) => (
  <IconButton onClick={onClick}>
    <MoreVert style={{ color: 'white' }} />
  </IconButton>
))`
  color: #6c96ae;
  cursor: pointer;
`;
import InfoPopover from 'Components/InfoPopover';

interface IStyleProps {
  inactive?: string;
  mode?: string;
}

const StyledSection = styled('div')({});

const StyledHeader = styled('div', {
  shouldForwardProp: prop => prop !== 'inactive' && prop !== 'mode',
})(({ inactive, mode }: IStyleProps) => ({
  margin: `0 -${styleDocument.padding}`,
  padding: `0 ${styleDocument.padding}`,
  height: mode === 'noBar' ? '2rem' : '5rem',
  backgroundColor: mode === 'noBar' ? 'transparent' : !inactive ? colors.primary : colors.quaternary,
  color: mode === 'noBar' ? colors.primary : colors.white,
  fontSize: '2rem',
  fontWeight: 600,
}));

const SectionContent = styled('div', { shouldForwardProp: prop => prop !== 'hasHistoryRows' })(
  ({ hasHistoryRows }: { hasHistoryRows?: boolean }) => ({
    padding: hasHistoryRows ? '1rem 0 0 0' : '3rem 0 4rem 0',
    '&:empty': {
      padding: '3rem 0 4rem 0',
    },
  }),
);

// 4.5rem margin for HistoryRowControls three dots menu button
const ButtonArea = styled('div', { shouldForwardProp: prop => prop !== 'controlsMargin' })(
  ({ controlsMargin }: { controlsMargin: boolean }) => ({
    marginRight: controlsMargin ? '4.5rem' : '0',
  }),
);

const InfoText = styled('div')({
  fontWeight: 'normal',
  fontSize: '1.4rem',
  color: styleDocument.documentHeader.infoTextColor,
});

const HistorySection = ({
  headerText,
  headerStyle,
  headerContentStyle = {},
  children,
  inactive = false,
  mode = 'default',
  newButton,
  hasHistoryRows = false,
  sectionInfoKey,
  menuItems,
  headerInfoText,
}: IOwnProps): React.JSX.Element => {
  const headerId = React.useRef(createID());
  const headerElement = document.getElementById(headerId.current);

  const [anchor, setAnchor] = React.useState<null | Element | Element>(null);

  const toggleMenu = (e: React.MouseEvent<Element>): void => {
    if (anchor) blurActiveElement();
    setAnchor(!anchor ? e.currentTarget : null);
  };

  const visibleMenuItems = (menuItems ?? []).filter(Boolean);

  return (
    <StyledSection>
      <StyledHeader mode={mode} inactive={inactive ? 'true' : undefined} style={headerStyle}>
        <Container style={{ height: '100%', alignItems: 'center', ...headerContentStyle }}>
          <Item
            id={headerId.current}
            xs={true}
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {headerText &&
              (headerElement && headerElement?.scrollWidth > headerElement?.clientWidth ? (
                <ToolTip
                  title={''}
                  description={typeof headerText === 'number' ? headerText.toString() : headerText}
                  content={<span>{typeof headerText === 'number' ? headerText.toString() : headerText}</span>}
                  hover={true}
                />
              ) : (
                <Container>
                  <Item>{headerText}</Item>
                  {headerInfoText && (
                    <Item style={{ paddingLeft: '2rem' }}>
                      <InfoPopover text={headerInfoText} />
                    </Item>
                  )}
                </Container>
              ))}
          </Item>
          {newButton && (
            <Item>
              <Container justifyContent="flex-end">
                <Item>
                  <ButtonArea controlsMargin={visibleMenuItems.length === 0}>{newButton}</ButtonArea>
                </Item>
              </Container>
            </Item>
          )}
          {visibleMenuItems.length > 0 && (
            <Item style={{ marginLeft: '0.5rem' }}>
              <StyledMenuIcon onClick={toggleMenu} />
              <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={toggleMenu}>
                {visibleMenuItems}
              </Menu>
            </Item>
          )}
        </Container>
      </StyledHeader>
      {sectionInfoKey && (
        <Container style={{ marginBlock: '2rem' }}>
          <Item xs={7}>
            <InfoText>
              <FormattedMessage id={sectionInfoKey} />
            </InfoText>
          </Item>
        </Container>
      )}
      <SectionContent hasHistoryRows={hasHistoryRows}>{children}</SectionContent>
    </StyledSection>
  );
};

interface IOwnProps {
  headerText?: string | number | React.JSX.Element;
  /** Style for the header component - recommended to be used only when mode is 'noBar' */
  headerStyle?: { [key: string]: string | number };
  /** Style for the Container element inside header - recommended to be used only when mode is 'noBar' */
  headerContentStyle?: { [key: string]: string | number };
  children: React.JSX.Element | Array<React.JSX.Element | undefined> | undefined;
  inactive?: boolean;
  mode?: 'default' | 'noBar';
  newButton?: React.JSX.Element;
  hasHistoryRows?: boolean; // If the HistorySection has HistoryRows, then have a smaller margin after section header
  sectionInfoKey?: string;
  menuItems?: React.JSX.Element[];

  /** Render info icon and popup with text */
  headerInfoText?: string | React.JSX.Element;
}

export default HistorySection;
